import React, { useState, useEffect } from "react";
import { Box, Typography, MenuItem } from "@mui/material";
import { styled } from "@mui/material/styles";
import { Search, Users, Trash, ChevronDown } from "lucide-react";
import { CustomTextField } from "./CustomTextField.tsx";
import { User, AdminUsersTabProps, AlertConfig } from "./types";

interface StyledProps {
  $isDark?: boolean;
  $variant?: "primary" | "danger";
  disabled?: boolean;
}

const Container = styled(Box)<StyledProps>`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SearchContainer = styled(Box)<StyledProps>`
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 16px;
`;

const UsersList = styled(Box)<StyledProps>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 400px;
  overflow-y: auto;
  padding: 8px;
  background-color: ${props => props.$isDark ? '#18181B' : '#F9FAFB'};
  border-radius: 8px;
  border: 1px solid ${props => props.$isDark ? '#27272A' : '#E5E7EB'};
`;

const UserItem = styled(Box)<StyledProps & { $selected?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background-color: ${props => 
    props.$selected 
      ? (props.$isDark ? 'rgba(34, 197, 94, 0.1)' : 'rgba(22, 163, 74, 0.1)')
      : (props.$isDark ? '#27272A' : '#FFFFFF')
  };
  border: 1px solid ${props => 
    props.$selected 
      ? (props.$isDark ? '#22C55E' : '#16A34A') 
      : (props.$isDark ? '#3F3F46' : '#E5E7EB')
  };
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${props => 
      props.$selected 
        ? (props.$isDark ? 'rgba(34, 197, 94, 0.15)' : 'rgba(22, 163, 74, 0.15)')
        : (props.$isDark ? '#3F3F46' : '#F3F4F6')
    };
  }
`;

const UserInfo = styled(Box)<StyledProps>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`;

const UserName = styled(Typography)<StyledProps>`
  color: ${props => props.$isDark ? '#FFFFFF' : '#1F2937'};
  font-weight: 600;
  font-size: 14px;
`;

const UserEmail = styled(Typography)<StyledProps>`
  color: ${props => props.$isDark ? '#9CA3AF' : '#6B7280'};
  font-size: 12px;
`;

const UserGroups = styled(Box)<StyledProps>`
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  margin-top: 4px;
`;

const GroupBadge = styled(Box)<StyledProps>`
  background-color: ${props => props.$isDark ? '#22C55E' : '#16A34A'};
  color: #FFFFFF;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const UserActions = styled(Box)<StyledProps>`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const ActionButton = styled(Box)<StyledProps & { $color?: string }>`
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.$color ? props.$color : 'transparent'};
  color: ${props => 
    props.$color ? '#FFFFFF' : (props.$isDark ? '#E5E7EB' : '#4B5563')
  };
  transition: background-color 0.2s;

  &:hover {
    background-color: ${props => 
      props.$color === '#EF4444' 
        ? '#DC2626' 
        : (props.$isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)')
    };
  }
`;

const UserDetails = styled(Box)<StyledProps>`
  background-color: ${props => props.$isDark ? '#27272A' : '#FFFFFF'};
  border: 1px solid ${props => props.$isDark ? '#3F3F46' : '#E5E7EB'};
  border-radius: 8px;
  padding: 20px;
  margin-top: 16px;
`;

const DetailRow = styled(Box)<StyledProps>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid ${props => props.$isDark ? '#3F3F46' : '#E5E7EB'};

  &:last-child {
    border-bottom: none;
  }
`;

const DetailLabel = styled(Typography)<StyledProps>`
  color: ${props => props.$isDark ? '#9CA3AF' : '#6B7280'};
  font-size: 14px;
  font-weight: 500;
`;

const DetailValue = styled(Typography)<StyledProps>`
  color: ${props => props.$isDark ? '#FFFFFF' : '#1F2937'};
  font-size: 14px;
`;

const NoUsersMessage = styled(Box)<StyledProps>`
  text-align: center;
  padding: 40px 20px;
  color: ${props => props.$isDark ? '#9CA3AF' : '#6B7280'};
`;

const LoadingMessage = styled(Box)<StyledProps>`
  text-align: center;
  padding: 20px;
  color: ${props => props.$isDark ? '#9CA3AF' : '#6B7280'};
`;

const AdminUsersTab: React.FC<AdminUsersTabProps> = ({
  users,
  userData,
  updateUserData,
  handleDeleteUser,
  isLoading,
  isDarkMode,
  textConfig,
  fetchError,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);

  useEffect(() => {
    if (users) {
      const filtered = users.filter(user =>
        user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ((user.email || user.attributes?.email) && 
         (user.email || user.attributes?.email)!.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredUsers(filtered);
    }
  }, [users, searchTerm]);

  const handleUserSelect = (user: User) => {
    updateUserData(user);
  };

  const handleDeleteClick = (username: string, event: React.MouseEvent) => {
    event.stopPropagation();
    handleDeleteUser(username);
  };

  if (isLoading) {
    return (
      <Container $isDark={isDarkMode}>
        <LoadingMessage $isDark={isDarkMode}>
          {textConfig.ADMIN_USERS_TAB_LOADING || "Cargando usuarios..."}
        </LoadingMessage>
      </Container>
    );
  }

  if (fetchError) {
    return (
      <Container $isDark={isDarkMode}>
        <Box
          sx={{
            textAlign: 'center',
            padding: '40px 20px',
            backgroundColor: isDarkMode ? '#27272A' : '#FEF2F2',
            border: `1px solid ${isDarkMode ? '#EF4444' : '#FECACA'}`,
            borderRadius: '8px',
            color: isDarkMode ? '#FCA5A5' : '#DC2626'
          }}
        >
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            {textConfig.ADMIN_USERS_TAB_ERROR_TITLE || "Error al cargar usuarios"}
          </Typography>
          <Typography sx={{ mb: 2, fontSize: '14px' }}>
            {fetchError.includes('500') 
              ? (textConfig.ADMIN_USERS_TAB_ERROR_500 || "Error del servidor. Verifique que el servicio esté funcionando correctamente.")
              : fetchError
            }
          </Typography>
          <Typography sx={{ fontSize: '12px', opacity: 0.8 }}>
            {textConfig.ADMIN_USERS_TAB_ERROR_SUGGESTION || "Intente recargar la página o contacte al administrador del sistema."}
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container $isDark={isDarkMode}>
      <SearchContainer $isDark={isDarkMode}>
        <Box sx={{ position: 'relative', flex: 1 }}>
          <Search 
            size={20} 
            style={{ 
              position: 'absolute', 
              left: 12, 
              top: '50%', 
              transform: 'translateY(-50%)',
              color: isDarkMode ? '#9CA3AF' : '#6B7280'
            }} 
          />
          <CustomTextField
            isFullWidth
            placeholder={textConfig.ADMIN_USERS_TAB_SEARCH_PLACEHOLDER || "Buscar por nombre de usuario o email..."}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              '& .MuiInputBase-root': {
                paddingLeft: '44px',
                backgroundColor: isDarkMode ? '#18181B' : '#FFFFFF',
              }
            }}
          />
        </Box>
      </SearchContainer>

      {filteredUsers.length === 0 ? (
        <NoUsersMessage $isDark={isDarkMode}>
          {searchTerm 
            ? (textConfig.ADMIN_USERS_TAB_NO_RESULTS || "No se encontraron usuarios que coincidan con la búsqueda")
            : (textConfig.ADMIN_USERS_TAB_NO_USERS || "No hay usuarios disponibles")
          }
        </NoUsersMessage>
      ) : (
        <UsersList $isDark={isDarkMode}>
          {filteredUsers.map((user) => (
            <UserItem
              key={user.username}
              $isDark={isDarkMode}
              $selected={userData?.username === user.username}
              onClick={() => handleUserSelect(user)}
            >
              <UserInfo $isDark={isDarkMode}>
                <UserName $isDark={isDarkMode}>{user.username}</UserName>
                {(user.email || user.attributes?.email) && (
                  <UserEmail $isDark={isDarkMode}>
                    {user.email || user.attributes?.email}
                  </UserEmail>
                )}
                {user.groups && user.groups.length > 0 && (
                  <UserGroups $isDark={isDarkMode}>
                    {user.groups.map((group) => (
                      <GroupBadge key={group} $isDark={isDarkMode}>
                        <Users size={12} />
                        {group === 'admin' ? 'Administrador' : 'Usuario'}
                      </GroupBadge>
                    ))}
                  </UserGroups>
                )}
              </UserInfo>
              
              <UserActions $isDark={isDarkMode}>
                <ActionButton
                  $isDark={isDarkMode}
                  $color="#EF4444"
                  onClick={(e) => handleDeleteClick(user.username, e)}
                  title={textConfig.ADMIN_USERS_TAB_DELETE_USER || "Eliminar usuario"}
                >
                  <Trash size={16} />
                </ActionButton>
              </UserActions>
            </UserItem>
          ))}
        </UsersList>
      )}
      {userData && (
        <UserDetails $isDark={isDarkMode}>
          <Typography
            variant="h6"
            sx={{
              color: isDarkMode ? '#FFFFFF' : '#1F2937',
              marginBottom: 2,
              fontWeight: 600
            }}
          >
            {textConfig.ADMIN_USERS_TAB_USER_DETAILS || "Detalles del Usuario"}
          </Typography>

          <DetailRow $isDark={isDarkMode}>
            <DetailLabel $isDark={isDarkMode}>
              {textConfig.ADMIN_USERS_TAB_USERNAME || "Nombre de usuario"}
            </DetailLabel>
            <DetailValue $isDark={isDarkMode}>{userData.username}</DetailValue>
          </DetailRow>

          {(userData.email || userData.attributes?.email) && (
            <DetailRow $isDark={isDarkMode}>
              <DetailLabel $isDark={isDarkMode}>
                {textConfig.ADMIN_USERS_TAB_EMAIL || "Email"}
              </DetailLabel>
              <DetailValue $isDark={isDarkMode}>
                {userData.email || userData.attributes?.email}
              </DetailValue>
            </DetailRow>
          )}
        </UserDetails>
      )}
    </Container>
  );
};

export default AdminUsersTab;